import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import useLanguage from '../hooks/useLanguage';

const { FiBookOpen, FiGlobe, FiSearch } = FiIcons;

const LoadingScreen = ({ onComplete }) => {
  const { t } = useLanguage();

  const logoVariants = {
    initial: { scale: 0, rotate: -180 },
    animate: { 
      scale: 1, 
      rotate: 0, 
      transition: { type: "spring", stiffness: 260, damping: 20, duration: 1.5 } 
    }
  };

  const textVariants = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0, transition: { delay: 0.8, duration: 0.8 } }
  };

  const iconVariants = {
    initial: { opacity: 0, scale: 0 },
    animate: { 
      opacity: 1, 
      scale: 1, 
      transition: { delay: 1.2, duration: 0.5, staggerChildren: 0.1 } 
    }
  };

  React.useEffect(() => {
    const timer = setTimeout(() => {
      onComplete();
    }, 3000);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <motion.div 
      className="fixed inset-0 bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-700 flex items-center justify-center z-50"
      initial={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.8 } }}
    >
      <div className="text-center px-4">
        <motion.div className="mb-6 sm:mb-8" variants={logoVariants} initial="initial" animate="animate">
          <div className="w-16 h-16 sm:w-24 sm:h-24 mx-auto bg-white rounded-xl sm:rounded-2xl flex items-center justify-center shadow-2xl">
            <SafeIcon icon={FiBookOpen} className="text-2xl sm:text-4xl text-blue-600" />
          </div>
        </motion.div>

        <motion.div variants={textVariants} initial="initial" animate="animate" className="text-white mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-2">
            {t('loading.title')}
          </h1>
          <p className="text-sm sm:text-base lg:text-lg opacity-90">
            {t('loading.subtitle')}
          </p>
        </motion.div>

        <motion.div variants={iconVariants} initial="initial" animate="animate" className="flex justify-center space-x-3 sm:space-x-4">
          <motion.div variants={iconVariants}>
            <SafeIcon icon={FiGlobe} className="text-xl sm:text-2xl text-white opacity-70" />
          </motion.div>
          <motion.div variants={iconVariants}>
            <SafeIcon icon={FiSearch} className="text-xl sm:text-2xl text-white opacity-70" />
          </motion.div>
          <motion.div variants={iconVariants}>
            <SafeIcon icon={FiBookOpen} className="text-xl sm:text-2xl text-white opacity-70" />
          </motion.div>
        </motion.div>

        <motion.div className="mt-6 sm:mt-8" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2, duration: 0.5 }}>
          <div className="w-12 h-1 sm:w-16 bg-white/30 rounded-full mx-auto overflow-hidden">
            <motion.div 
              className="h-full bg-white rounded-full"
              initial={{ width: 0 }}
              animate={{ width: '100%' }}
              transition={{ delay: 2, duration: 0.8 }}
            />
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default LoadingScreen;